/**
 * NumberRangeFilter Komponenti
 * 
 * Capacity gibi sayısal değerler için min-max number input
 * Range slider yerine number input kullanır (daha UX friendly)
 */

'use client'

import { useState, useCallback, useEffect } from 'react'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'

export interface NumberRangeFilterProps {
    fieldKey: string
    label: string
    min: number
    max: number
    step?: number
    value?: { min: number; max: number }
    onChange?: (value: { min: number; max: number }) => void
    unit?: string // "cabin", "guest", "WC" gibi
    className?: string
}

export default function NumberRangeFilter({
    fieldKey,
    label,
    min,
    max,
    step = 1,
    value,
    onChange,
    unit,
    className = '',
}: NumberRangeFilterProps) {
    const { getVariable } = useLanguageVariables()
    const [localMin, setLocalMin] = useState(value?.min ?? min)
    const [localMax, setLocalMax] = useState(value?.max ?? max)

    // Value prop değiştiğinde local state'i güncelle
    useEffect(() => {
        if (value) {
            setLocalMin(value.min)
            setLocalMax(value.max)
        }
    }, [value])

    const handleMinChange = useCallback(
        (newMin: number) => {
            const clampedMin = Math.max(min, Math.min(newMin, localMax))
            setLocalMin(clampedMin)
            onChange?.({ min: clampedMin, max: localMax })
        },
        [min, localMax, onChange]
    )

    const handleMaxChange = useCallback(
        (newMax: number) => {
            const clampedMax = Math.min(max, Math.max(newMax, localMin))
            setLocalMax(clampedMax)
            onChange?.({ min: localMin, max: clampedMax })
        },
        [max, localMin, onChange]
    )

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Label */}
            {label && (
                <label className="block text-sm font-medium text-alsero-dark-gray">
                    {label}
                </label>
            )}
            
            {/* Min-Max Number Inputs */}
            <div className="flex items-center gap-3">
                {/* Min Input */}
                <div className="flex-1">
                    <label className="block text-xs text-alsero-light-gray mb-1.5">
                        {getVariable('filter_min', 'Min')}
                    </label>
                    <input
                        type="number"
                        min={min}
                        max={localMax}
                        step={step}
                        value={localMin}
                        onChange={(e) => handleMinChange(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-alsero-dark-gray bg-white focus:outline-none focus:ring-2 focus:ring-alsero-first focus:border-alsero-first transition-colors"
                        placeholder={String(min)}
                    />
                </div>

                {/* Max Input */}
                <div className="flex-1">
                    <label className="block text-xs text-alsero-light-gray mb-1.5">
                        {getVariable('filter_max', 'Max')}
                    </label>
                    <input
                        type="number"
                        min={localMin}
                        max={max}
                        step={step}
                        value={localMax}
                        onChange={(e) => handleMaxChange(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-alsero-dark-gray bg-white focus:outline-none focus:ring-2 focus:ring-alsero-first focus:border-alsero-first transition-colors"
                        placeholder={String(max)}
                    />
                </div>
            </div>

            {/* Unit display (optional) */}
            {unit && (
                <div className="text-xs text-alsero-light-gray text-center">
                    {getVariable('filter_unit', 'Unit')}: {unit}
                </div>
            )}
        </div>
    )
}

